// src/pages/Orders.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    Loader2,
    ShoppingCart,
    Package,
    ChevronRight,
    ChevronLeft,
    Filter as FilterIcon,
    Sparkles,
    Info,
    CreditCard,
    Wallet,
    Clock,
    CheckCircle2,
    XCircle,
    FileText
} from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from '@/components/ui/dialog';
import {
    Select,
    SelectTrigger,
    SelectContent,
    SelectItem,
    SelectValue
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';

import { ordersAPI, toSafeNumber } from '../services/api';
import { toast } from 'sonner';

// 📅 Jalali Date Picker
import DatePicker from 'react-multi-date-picker';
import DateObject from 'react-date-object';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';

// ---------- انواع داده ----------
type OrderItem = {
    id: number | string;
    product_id: number | string;
    product_title?: string | null;
    quantity?: number;
    amount: string | number;
    status: string; // pending_payment | paid | cancelled ...
    invoice_status?: string | null; // pending | paid ...
    invoice_id?: number | string | null;
    created_at: string;
};

type Filters = {
    status: string;
    q: string;
    from: Date | null;
    to: Date | null;
};

// ---------- فرمت کمکی ----------
const faNumber = (n: number | string) =>
    new Intl.NumberFormat('fa-IR').format(
        typeof n === 'string' ? parseFloat(n) || 0 : n
    );

const formatFaDateTime = (d: string | number | Date) =>
    new Date(d).toLocaleString('fa-IR');

const statusClass = (status: string) => {
    switch (status) {
        case 'paid':
            return 'bg-emerald-600 text-white';
        case 'pending_payment':
            return 'bg-amber-500 text-white';
        case 'cancelled':
            return 'bg-red-600 text-white';
        default:
            return 'bg-gray-200 text-gray-700 dark:bg-slate-600 dark:text-slate-100';
    }
};

const statusLabel = (status: string) => {
    switch (status) {
        case 'paid':
            return 'پرداخت‌شده';
        case 'pending_payment':
            return 'در انتظار پرداخت';
        case 'cancelled':
            return 'لغو شده';
        default:
            return status;
    }
};

const invoiceStatusBadge = (invStatus?: string | null) => {
    if (!invStatus) return null;
    let cls = '';
    let label = invStatus;

    switch (invStatus) {
        case 'paid':
            cls =
                'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-100 dark:border-emerald-700';
            label = 'فاکتور پرداخت‌شده';
            break;
        case 'pending':
            cls =
                'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/40 dark:text-amber-100 dark:border-amber-700';
            label = 'فاکتور در انتظار پرداخت';
            break;
        default:
            cls =
                'bg-gray-50 text-gray-700 border border-gray-200 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600';
            label = `فاکتور: ${invStatus}`;
    }

    return (
        <span className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded-full ${cls}`}>
            <CreditCard className="h-3 w-3 ml-1" />
            {label}
        </span>
    );
};

// ---------- کمک برای تبدیل Date → DateObject شمسی ----------
const toPersianDateObject = (d: Date | null) => {
    if (!d) return null;
    try {
        return new DateObject({
            date: d,
            calendar: persian,
            locale: persian_fa
        });
    } catch {
        return null;
    }
};

// ---------- کامپوننت اصلی ----------
const Orders: React.FC = () => {
    const { id: idParam, orderId } = useParams<{ id?: string; orderId?: string }>();
    const id = idParam || orderId;

    const navigate = useNavigate();

    const [orders, setOrders] = useState<OrderItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // صفحه‌بندی
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // فیلترها
    const [filters, setFilters] = useState<Filters>({
        status: 'all',
        q: '',
        from: null,
        to: null
    });
    const [applied, setApplied] = useState<Filters>(filters);

    const [filtersOpen, setFiltersOpen] = useState(false);

    // مودال جزئیات
    const [selectedOrder, setSelectedOrder] = useState<OrderItem | null>(null);
    const [detailsOpen, setDetailsOpen] = useState(false);

    // لودینگ لغو سفارش
    const [cancelLoadingId, setCancelLoadingId] = useState<string | number | null>(null);

    const closeModal = () => {
        setDetailsOpen(false);
        setSelectedOrder(null);
        if (id) {
            navigate('/orders', { replace: true });
        }
    };

    // ---------- دریافت لیست سفارش‌ها ----------
    const fetchOrders = async () => {
        setLoading(true);
        setError('');
        try {
            // ✅ استفاده از listOrders به جای getAllOrders
            const res = await ordersAPI.listOrders(1, 9999);

            const arr =
                Array.isArray(res)
                    ? res
                    : Array.isArray((res as any)?.items)
                        ? (res as any).items
                        : Array.isArray((res as any)?.orders)
                            ? (res as any).orders
                            : [];

            setOrders(arr as OrderItem[]);
        } catch (err: any) {
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                'خطا در دریافت سفارش‌ها';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    // ---------- گرفتن یک سفارش با /orders/:id ----------
    useEffect(() => {
        if (!id) {
            setDetailsOpen(false);
            setSelectedOrder(null);
            return;
        }

        let cancelled = false;

        const fetchSingleOrder = async () => {
            try {
                // ✅ تبدیل id به عدد با toSafeNumber
                const safeId = toSafeNumber(id, 0);
                if (safeId <= 0) {
                    setError('شناسه سفارش نامعتبر است.');
                    setLoading(false);
                    return;
                }

                const res = await ordersAPI.getOrderById(safeId);
                const order: OrderItem =
                    (res && (res as any).item && (res as any).item.id)
                        ? (res as any).item
                        : (res as OrderItem);

                if (cancelled) return;

                setSelectedOrder(order);
                setDetailsOpen(true);

                // اگر در لیست هست، صفحه را تنظیم کن
                const idx = orders.findIndex(o => String(o.id) === String(order.id));
                if (idx !== -1) {
                    const targetPage = Math.floor(idx / pageSize) + 1;
                    setPage(targetPage);
                }
            } catch (e) {
                console.error('fetch single order failed', e);
                if (!cancelled) {
                    setDetailsOpen(false);
                    setSelectedOrder(null);
                }
            }
        };

        fetchSingleOrder();

        return () => {
            cancelled = true;
        };
    }, [id, orders, pageSize]);

    // ---------- آمار بالا ----------
    const stats = useMemo(() => {
        const total = orders.length;
        const pendingPayment = orders.filter(o => o.status === 'pending_payment').length;
        const paid = orders.filter(o => o.status === 'paid').length;

        const totalAmountRial = orders.reduce((sum, o) => {
            const v = typeof o.amount === 'string' ? parseFloat(o.amount) || 0 : o.amount;
            return sum + v;
        }, 0);

        return { total, pendingPayment, paid, totalAmountRial };
    }, [orders]);

    // ---------- فیلتر ----------
    const filteredOrders = useMemo(() => {
        const base = Array.isArray(orders) ? orders : [];
        const q = (applied.q || '').trim().toLowerCase();
        const hasQ = q.length > 0;
        const from = applied.from;
        const to = applied.to;

        return base.filter(o => {
            if (applied.status !== 'all' && o.status !== applied.status) return false;

            if (hasQ) {
                const hay = `${o.id} ${o.product_id} ${o.product_title || ''}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }

            if (from || to) {
                const cd = new Date(o.created_at);
                if (from && cd < from) return false;
                if (to) {
                    const end = new Date(to);
                    end.setHours(23, 59, 59, 999);
                    if (cd > end) return false;
                }
            }

            return true;
        });
    }, [orders, applied]);

    // ---------- صفحه‌بندی ----------
    const totalItems = filteredOrders.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [totalPages, page]);

    const pagedOrders = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredOrders.slice(start, start + pageSize);
    }, [filteredOrders, page, pageSize]);

    const applyFilters = () => {
        setApplied(filters);
        setPage(1);
    };

    const clearFilters = () => {
        const reset: Filters = { status: 'all', q: '', from: null, to: null };
        setFilters(reset);
        setApplied(reset);
        setPage(1);
    };

    const openDetails = (order: OrderItem) => {
        setSelectedOrder(order);
        setDetailsOpen(true);
        navigate(`/orders/${order.id}`, { replace: false });
    };

    // ---------- DatePicker مقادیر ----------
    const fromValue = useMemo(
        () => toPersianDateObject(filters.from),
        [filters.from]
    );
    const toValue = useMemo(
        () => toPersianDateObject(filters.to),
        [filters.to]
    );

    const handleFromChange = (value: any) => {
        if (!value) {
            setFilters(f => ({ ...f, from: null }));
            return;
        }
        const v = Array.isArray(value) ? value[0] : value;
        const gDate = v?.toDate ? v.toDate() : new Date(v);
        setFilters(f => ({ ...f, from: gDate }));
    };

    const handleToChange = (value: any) => {
        if (!value) {
            setFilters(f => ({ ...f, to: null }));
            return;
        }
        const v = Array.isArray(value) ? value[0] : value;
        const gDate = v?.toDate ? v.toDate() : new Date(v);
        setFilters(f => ({ ...f, to: gDate }));
    };

    // ---------- لغو سفارش ----------
    const handleCancelOrder = async (order: OrderItem) => {
        if (order.status !== 'pending_payment') {
            toast.error('فقط سفارش‌های در انتظار پرداخت قابل لغو هستند.');
            return;
        }

        const ok = window.confirm(
            `آیا از لغو سفارش #${order.id} مطمئن هستید؟\nاین عمل قابل بازگشت نیست.`
        );
        if (!ok) return;

        try {
            setCancelLoadingId(order.id);
            // ✅ تبدیل به عدد با toSafeNumber
            const safeOrderId = toSafeNumber(order.id, 0);
            if (safeOrderId <= 0) {
                toast.error('شناسه سفارش نامعتبر است.');
                return;
            }
            await ordersAPI.cancelOrder(safeOrderId);

            toast.success('سفارش با موفقیت لغو شد.');

            // لیست را رفرش کن
            await fetchOrders();

            // اگر داخل مودال همین سفارش باز است، وضعیتش را هم آپدیت کن
            setSelectedOrder(prev =>
                prev && String(prev.id) === String(order.id)
                    ? { ...prev, status: 'cancelled' }
                    : prev
            );
        } catch (err: any) {
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                'خطا در لغو سفارش';
            toast.error(msg);
        } finally {
            setCancelLoadingId(null);
        }
    };

    // ---------- UI ----------
    if (loading) {
        return (
            <div
                className="flex items-center justify-center h-screen bg-gradient-to-br from-emerald-50 via-emerald-50 to-teal-50 dark:from-slate-900 dark:via-slate-950 dark:to-black"
                dir="rtl"
            >
                <div className="text-center">
                    <Loader2 className="h-12 w-12 animate-spin text-emerald-600 dark:text-emerald-400 mx-auto mb-4" />
                    <p className="text-emerald-700 dark:text-emerald-100 font-medium">
                        در حال بارگذاری سفارش‌ها...
                    </p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6" dir="rtl">
                <Alert className="dark:bg-slate-900 dark:text-slate-100 dark:border-red-500/60" variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
                <div className="mt-3">
                    <Button
                        variant="outline"
                        onClick={fetchOrders}
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500 dark:text-emerald-200 dark:hover:bg-slate-800"
                    >
                        تلاش مجدد
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div
            dir="rtl"
            className="
        min-h-screen space-y-6 p-4 md:p-8
        bg-gradient-to-br from-emerald-50 via-emerald-50 to-teal-50
        dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-950 dark:to-black
        dark:text-slate-100
      "
        >
            {/* Header */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-600 via-emerald-600 to-teal-600 p-6 md:p-8 shadow-2xl">
                <div className="absolute inset-0 opacity-15 dark:opacity-20 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')"/>
                <div className="relative flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="text-white space-y-2">
                        <div className="flex items-center gap-3">
                            <Sparkles className="h-8 w-8" />
                            <h1 className="text-2xl md:text-4xl font-bold">سفارش‌ها</h1>
                        </div>
                        <p className="text-emerald-100 text-sm md:text-lg">
                            در این بخش می‌توانید وضعیت سفارش‌ها و فاکتورهای خود را ببینید و پیگیری کنید.
                        </p>
                    </div>

                    {/* آمار دسکتاپ */}
                    <div className="hidden md:flex flex-wrap gap-3">
                        <div className="bg-white/15 backdrop-blur-sm rounded-xl px-5 py-3 text-white border border-white/30 min-w-[140px]">
                            <div className="text-xs opacity-90">کل سفارش‌ها</div>
                            <div className="text-2xl font-bold">{faNumber(stats.total)}</div>
                        </div>
                        <div className="bg-white/15 backdrop-blur-sm rounded-xl px-5 py-3 text-white border border-white/30 min-w-[140px]">
                            <div className="text-xs opacity-90 flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5" /> در انتظار پرداخت
                            </div>
                            <div className="text-2xl font-bold text-amber-200">
                                {faNumber(stats.pendingPayment)}
                            </div>
                        </div>
                        <div className="bg-white/15 backdrop-blur-sm rounded-xl px-5 py-3 text-white border border-white/30 min-w-[140px]">
                            <div className="text-xs opacity-90 flex items-center gap-1">
                                <CheckCircle2 className="h-3.5 w-3.5" /> پرداخت‌شده
                            </div>
                            <div className="text-2xl font-bold text-emerald-200">
                                {faNumber(stats.paid)}
                            </div>
                        </div>
                        <div className="bg-white/15 backdrop-blur-sm rounded-xl px-5 py-3 text-white border border-white/30 min-w-[140px]">
                            <div className="text-xs opacity-90 flex items-center gap-1">
                                <Wallet className="h-3.5 w-3.5" /> مجموع مبالغ
                            </div>
                            <div className="text-lg font-bold">
                                {faNumber(stats.totalAmountRial / 10)} <span className="text-sm">تومان</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* آمار موبایل */}
                <div className="relative mt-4 grid grid-cols-2 gap-3 md:hidden text-white text-xs">
                    <div className="bg-white/10 rounded-xl px-3 py-2">
                        <div className="opacity-80 mb-1">کل سفارش‌ها</div>
                        <div className="text-lg font-bold">{faNumber(stats.total)}</div>
                    </div>
                    <div className="bg-white/10 rounded-xl px-3 py-2">
                        <div className="opacity-80 mb-1 flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" /> در انتظار پرداخت
                        </div>
                        <div className="text-lg font-bold text-amber-200">
                            {faNumber(stats.pendingPayment)}
                        </div>
                    </div>
                    <div className="bg-white/10 rounded-xl px-3 py-2">
                        <div className="opacity-80 mb-1 flex items-center gap-1">
                            <CheckCircle2 className="h-3.5 w-3.5" /> پرداخت‌شده
                        </div>
                        <div className="text-lg font-bold text-emerald-200">
                            {faNumber(stats.paid)}
                        </div>
                    </div>
                    <div className="bg-white/10 rounded-xl px-3 py-2">
                        <div className="opacity-80 mb-1 flex items-center gap-1">
                            <Wallet className="h-3.5 w-3.5" /> مجموع مبالغ
                        </div>
                        <div className="text-base font-bold">
                            {faNumber(stats.totalAmountRial / 10)} <span className="text-[11px]">تومان</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* فیلترها */}
            <Card className="border-emerald-200/60 shadow-lg bg-white/85 backdrop-blur-sm dark:bg-slate-900/80 dark:border-slate-700">
                <CardHeader className="py-4 bg-gradient-to-r from-emerald-50 to-emerald-50/70 dark:from-slate-800 dark:to-slate-800/70">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-xl bg-emerald-600 flex items-center justify-center shadow-sm">
                                <FilterIcon className="h-5 w-5 text-white" />
                            </div>
                            <div>
                                <CardTitle className="text-emerald-900 dark:text-emerald-100 text-lg">فیلتر سفارش‌ها</CardTitle>
                                <CardDescription className="text-emerald-700 dark:text-emerald-200/80">
                                    فیلتر بر اساس وضعیت، شناسه، نام محصول و بازه‌ی زمانی
                                </CardDescription>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <Button
                                variant="outline"
                                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-xl dark:border-emerald-500 dark:text-emerald-100 dark:hover:bg-slate-800"
                                onClick={() => setFiltersOpen(v => !v)}
                            >
                                {filtersOpen ? 'بستن فیلترها' : 'نمایش فیلترها'}
                            </Button>
                            <Button
                                variant="outline"
                                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-xl dark:border-emerald-500 dark:text-emerald-100 dark:hover:bg-slate-800"
                                onClick={fetchOrders}
                            >
                                به‌روزرسانی
                            </Button>
                        </div>
                    </div>
                </CardHeader>

                {filtersOpen && (
                    <CardContent className="space-y-4 pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                            {/* وضعیت */}
                            <div className="space-y-2">
                                <span className="text-sm text-emerald-900 dark:text-emerald-100 font-medium">وضعیت سفارش</span>
                                <Select
                                    value={filters.status}
                                    onValueChange={v => setFilters(f => ({ ...f, status: v }))}
                                >
                                    <SelectTrigger className="border-emerald-300 focus:ring-emerald-500 focus:ring-2 rounded-xl dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100">
                                        <SelectValue placeholder="همه" />
                                    </SelectTrigger>
                                    <SelectContent className="dark:bg-slate-900 dark:text-slate-100">
                                        <SelectItem value="all">🔘 همه</SelectItem>
                                        <SelectItem value="pending_payment">⏳ در انتظار پرداخت</SelectItem>
                                        <SelectItem value="paid">✅ پرداخت‌شده</SelectItem>
                                        <SelectItem value="cancelled">❌ لغو شده</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* جستجو */}
                            <div className="space-y-2 md:col-span-2">
                                <span className="text-sm text-emerald-900 dark:text-emerald-100 font-medium">جستجو</span>
                                <Input
                                    value={filters.q}
                                    onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
                                    placeholder="شناسه، نام محصول یا شناسه محصول..."
                                    className="border-emerald-300 focus:ring-emerald-500 focus:ring-2 rounded-xl dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>

                            {/* از تاریخ */}
                            <div className="space-y-2">
                                <span className="text-sm text-emerald-900 dark:text-emerald-100 font-medium">از تاریخ</span>
                                <DatePicker
                                    value={fromValue || undefined}
                                    onChange={handleFromChange}
                                    calendar={persian}
                                    locale={persian_fa}
                                    inputClass="w-full rounded-xl border border-emerald-300 px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                    containerClassName="w-full"
                                    placeholder="انتخاب تاریخ"
                                    calendarPosition="bottom-right"
                                    portal
                                    style={{ width: '100%', zIndex: 9999 }}
                                />
                            </div>

                            {/* تا تاریخ */}
                            <div className="space-y-2">
                                <span className="text-sm text-emerald-900 dark:text-emerald-100 font-medium">تا تاریخ</span>
                                <DatePicker
                                    value={toValue || undefined}
                                    onChange={handleToChange}
                                    calendar={persian}
                                    locale={persian_fa}
                                    inputClass="w-full rounded-xl border border-emerald-300 px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                    containerClassName="w-full"
                                    placeholder="انتخاب تاریخ"
                                    calendarPosition="bottom-right"
                                    portal
                                    style={{ width: '100%', zIndex: 9999 }}
                                />
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-2 justify-start">
                            <Button
                                onClick={applyFilters}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                            >
                                اعمال فیلتر
                            </Button>
                            <Button
                                variant="outline"
                                onClick={clearFilters}
                                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-xl dark:border-emerald-500 dark:text-emerald-100 dark:hover:bg-slate-800"
                            >
                                حذف فیلترها
                            </Button>
                            <div className="text-xs sm:text-sm text-emerald-700 dark:text-emerald-100 sm:mr-auto sm:text-left bg-emerald-50/60 dark:bg-slate-800/70 border border-emerald-100 dark:border-slate-600 px-3 py-1.5 rounded-xl flex items-center gap-2">
                                <Info className="h-4 w-4" />
                                {faNumber(totalItems)} سفارش مطابق فیلترها
                            </div>
                        </div>
                    </CardContent>
                )}
            </Card>

            {/* لیست سفارش‌ها */}
            {orders.length === 0 ? (
                <Card className="shadow-lg border-emerald-100 bg-white/80 backdrop-blur-sm dark:bg-slate-900/80 dark:border-slate-700">
                    <CardContent className="text-center py-16">
                        <ShoppingCart className="h-16 w-16 mx-auto mb-4 text-emerald-200 dark:text-slate-600" />
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-2">
                            هنوز سفارشی ثبت نشده است
                        </h3>
                        <p className="text-gray-500 dark:text-slate-300">
                            وقتی سفارشی ثبت کنید، لیست آن در این بخش نمایش داده می‌شود.
                        </p>
                    </CardContent>
                </Card>
            ) : filteredOrders.length === 0 ? (
                <Card className="shadow-lg border-emerald-600/60 bg-white/80 backdrop-blur-sm dark:bg-slate-900/80 dark:border-slate-700">
                    <CardContent className="text-center py-12">
                        <XCircle className="h-12 w-12 mx-auto mb-3 text-amber-300 dark:text-amber-400" />
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-1">
                            موردی با این فیلترها پیدا نشد
                        </h3>
                        <p className="text-gray-500 dark:text-slate-300 mb-3">فیلترها یا متن جستجو را تغییر دهید.</p>
                        <Button
                            variant="outline"
                            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-xl dark:border-emerald-500 dark:text-emerald-100 dark:hover:bg-slate-800"
                            onClick={clearFilters}
                        >
                            ریست فیلترها
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-4">
                    {pagedOrders.map(o => {
                        const amountRial = typeof o.amount === 'string' ? parseFloat(o.amount) || 0 : o.amount;
                        const qty = o.quantity || 1;

                        const isPending = o.status === 'pending_payment';
                        const isPaid = o.status === 'paid';

                        return (
                            <div
                                key={o.id}
                                className="
                  relative overflow-hidden rounded-2xl
                  border border-emerald-600/70
                  bg-gradient-to-br from-emerald-50/70 to-white
                  shadow-md hover:shadow-xl transition-all duration-300
                  dark:bg-slate-950 dark:border-slate-700
                "
                            >
                                <div className="flex flex-col md:flex-row md:items-stretch">
                                    <div className="flex-1 p-4 md:p-5 flex flex-col justify-between border-b md:border-b-0 md:border-l border-dashed border-emerald-100 dark:border-slate-700/80">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-right">
                                            {/* order & product */}
                                            <div className="space-y-2">
                                                <div className="text-[11px] text-gray-500 dark:text-slate-300 font-semibold">
                                                    سفارش و محصول
                                                </div>
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span
                                                        className="text-sm font-mono text-emerald-800 dark:text-emerald-200 bg-emerald-50 inline-flex px-2 py-1 rounded-md border border-emerald-100 dark:bg-slate-800 dark:border-slate-600"
                                                        dir="ltr"
                                                    >
                                                        #ORD-{o.id}
                                                    </span>
                                                    <span className="inline-flex items-center text-xs text-gray-700 dark:text-slate-100 bg-emerald-50/70 px-2 py-0.5 rounded-full border border-emerald-100 dark:bg-slate-800 dark:border-slate-600">
                                                        <Package className="h-3.5 w-3.5 ml-1" />
                                                        {o.product_title || `محصول #${o.product_id}`}
                                                    </span>
                                                </div>

                                                <div className="mt-1 text-[11px] text-gray-500 dark:text-slate-300">
                                                    تعداد:{' '}
                                                    <span className="font-semibold text-gray-900 dark:text-slate-100">
                                                        {faNumber(qty)}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* status */}
                                            <div className="space-y-2">
                                                <div className="text-[11px] text-gray-500 dark:text-slate-300 font-semibold">
                                                    وضعیت سفارش و فاکتور
                                                </div>
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <Badge
                                                        className={`text-xs px-3 py-1 rounded-full ${statusClass(o.status)}`}
                                                    >
                                                        {statusLabel(o.status)}
                                                    </Badge>
                                                    {invoiceStatusBadge(o.invoice_status)}
                                                </div>
                                                <div className="mt-2 text-[11px] text-gray-500 dark:text-slate-300">
                                                    تاریخ ثبت سفارش:{' '}
                                                    <span className="font-semibold text-gray-900 dark:text-slate-100">
                                                        {formatFaDateTime(o.created_at)}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* amount & actions */}
                                            <div className="space-y-2">
                                                <div className="text-[11px] text-gray-500 dark:text-slate-300 font-semibold">
                                                    مبلغ سفارش
                                                </div>
                                                <div className="rounded-xl bg-white border border-emerald-200 px-3 py-2 space-y-1 dark:bg-slate-900 dark:border-slate-700">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs text-gray-600 dark:text-slate-300">جمع کل:</span>
                                                        <span className="font-bold text-emerald-700 dark:text-emerald-300 text-lg">
                                                            {faNumber(amountRial / 10)} تومان
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-slate-300">
                                                        <span>مبلغ واحد:</span>
                                                        <span>{faNumber((amountRial / qty) / 10)} تومان</span>
                                                    </div>
                                                </div>

                                                {/* اکشن‌ها */}
                                                <div className="flex flex-wrap gap-2 mt-2">
                                                    {/* پرداخت فاکتور (اگر فاکتور دارد) */}
                                                    {o.invoice_id && (!o.invoice_status || o.invoice_status === 'pending') && (
                                                        <Button
                                                            variant="destructive"
                                                            className="flex-1 sm:flex-none min-w-[120px] flex items-center justify-center gap-2 rounded-xl"
                                                            onClick={() => navigate(`/invoices/${o.invoice_id}`)}
                                                            disabled={isPaid || o.status === 'cancelled'}
                                                        >
                                                            <FileText className="h-4 w-4" />
                                                            پرداخت فاکتور
                                                        </Button>
                                                    )}

                                                    {/* لغو سفارش (فقط در انتظار پرداخت) */}
                                                    {isPending && (
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            className="flex-1 sm:flex-none min-w-[120px] border-red-300 text-red-600 hover:bg-red-50 rounded-xl dark:border-red-500 dark:text-red-300 dark:hover:bg-slate-800 flex items-center justify-center gap-2"
                                                            onClick={() => handleCancelOrder(o)}
                                                            disabled={cancelLoadingId === o.id}
                                                        >
                                                            {cancelLoadingId === o.id ? (
                                                                <>
                                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                                    در حال لغو...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <XCircle className="h-4 w-4" />
                                                                    لغو سفارش
                                                                </>
                                                            )}
                                                        </Button>
                                                    )}

                                                    {/* جزئیات */}
                                                    <Button
                                                        variant="outline"
                                                        className="flex-1 sm:flex-none min-w-[120px] border-emerald-300 text-emerald-700 hover:bg-emerald-50 flex items-center justify-center gap-2 rounded-xl dark:border-emerald-500 dark:text-emerald-100 dark:hover:bg-slate-800"
                                                        onClick={() => openDetails(o)}
                                                    >
                                                        <ShoppingCart className="h-4 w-4" />
                                                        جزئیات سفارش
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* صفحه‌بندی */}
                    <Card className="border-emerald-200/60 shadow-lg bg-white/85 backdrop-blur-sm dark:bg-slate-900/80 dark:border-slate-700">
                        <CardContent className="py-4">
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-600 dark:text-slate-200 font-medium">نمایش:</span>
                                    <Select
                                        value={String(pageSize)}
                                        onValueChange={v => {
                                            setPageSize(parseInt(v, 10));
                                            setPage(1);
                                        }}
                                    >
                                        <SelectTrigger className="h-10 w-24 rounded-xl border border-emerald-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="dark:bg-slate-900 dark:text-slate-100">
                                            <SelectItem value="5">۵ مورد</SelectItem>
                                            <SelectItem value="10">۱۰ مورد</SelectItem>
                                            <SelectItem value="20">۲۰ مورد</SelectItem>
                                            <SelectItem value="50">۵۰ مورد</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="text-sm text-gray-700 dark:text-slate-200 text-center font-medium bg-emerald-50 px-4 py-2 rounded-xl border border-emerald-100 dark:bg-slate-800 dark:border-slate-600">
                                    صفحه{' '}
                                    <span className="text-emerald-700 dark:text-emerald-300 font-bold">{faNumber(page)}</span>{' '}
                                    از{' '}
                                    <span className="text-emerald-700 dark:text-emerald-300 font-bold">{faNumber(totalPages)}</span>
                                    {' — '}
                                    <span className="text-emerald-700 dark:text-emerald-300 font-bold">
                                        {faNumber(totalItems)}
                                    </span>{' '}
                                    سفارش
                                </div>

                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page <= 1}
                                        className="flex-1 sm:flex-none border-emerald-400 text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 transition-all rounded-xl dark:border-emerald-500 dark:text-emerald-100 dark:hover:bg-slate-800"
                                    >
                                        <ChevronRight className="ml-1 h-4 w-4" />
                                        قبلی
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                        disabled={page >= totalPages}
                                        className="flex-1 sm:flex-none border-emerald-400 text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 transition-all rounded-xl dark:border-emerald-500 dark:text-emerald-100 dark:hover:bg-slate-800"
                                    >
                                        بعدی
                                        <ChevronLeft className="mr-1 h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* مودال جزئیات */}
            <Dialog
                open={detailsOpen}
                onOpenChange={(open) => {
                    setDetailsOpen(open);
                    if (!open) {
                        setSelectedOrder(null);
                        if (id) {
                            navigate('/orders', { replace: true });
                        }
                    }
                }}
            >
                <DialogContent
                    dir="rtl"
                    className="sm:max-w-lg rounded-2xl p-6 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border border-emerald-100 dark:border-slate-700"
                >
                    <DialogHeader className="text-right border-b pb-2 mb-3 border-emerald-100 dark:border-slate-700">
                        <DialogTitle className="text-xl font-semibold text-emerald-700 dark:text-emerald-300">
                            جزئیات سفارش #{selectedOrder?.id}
                        </DialogTitle>
                    </DialogHeader>

                    {selectedOrder ? (
                        <div className="space-y-3 text-right text-sm">
                            <div className="flex justify-between border-b pb-1 border-emerald-50 dark:border-slate-700">
                                <span className="text-gray-500 dark:text-slate-300">محصول:</span>
                                <span>
                                    {selectedOrder.product_title || `محصول #${selectedOrder.product_id}`}
                                </span>
                            </div>
                            <div className="flex justify-between border-b pb-1 border-emerald-50 dark:border-slate-700">
                                <span className="text-gray-500 dark:text-slate-300">تعداد:</span>
                                <span>{faNumber(selectedOrder.quantity || 1)}</span>
                            </div>
                            <div className="flex justify-between border-b pb-1 border-emerald-50 dark:border-slate-700">
                                <span className="text-gray-500 dark:text-slate-300">مبلغ کل:</span>
                                <span className="font-semibold text-emerald-600 dark:text-emerald-300">
                                    {faNumber(
                                        (typeof selectedOrder.amount === 'string'
                                            ? parseFloat(selectedOrder.amount) || 0
                                            : selectedOrder.amount) / 10
                                    )}{' '}
                                    تومان
                                </span>
                            </div>
                            <div className="flex justify-between border-b pb-1 border-emerald-50 dark:border-slate-700">
                                <span className="text-gray-500 dark:text-slate-300">وضعیت سفارش:</span>
                                <Badge className={statusClass(selectedOrder.status)}>
                                    {statusLabel(selectedOrder.status)}
                                </Badge>
                            </div>
                            {selectedOrder.invoice_status && (
                                <div className="flex justify-between border-b pb-1 border-emerald-50 dark:border-slate-700">
                                    <span className="text-gray-500 dark:text-slate-300">وضعیت فاکتور:</span>
                                    {invoiceStatusBadge(selectedOrder.invoice_status)}
                                </div>
                            )}
                            <div className="flex justify-between">
                                <span className="text-gray-500 dark:text-slate-300">تاریخ ثبت:</span>
                                <span>{formatFaDateTime(selectedOrder.created_at)}</span>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-500 dark:text-slate-300">در حال بارگذاری...</div>
                    )}

                    <DialogFooter className="mt-4 flex flex-wrap justify-end gap-2">
                        {selectedOrder?.invoice_id && (
                            <Button
                                type="button"
                                variant="outline"
                                className="border-emerald-400 text-emerald-700 hover:bg-emerald-50 rounded-xl flex items-center gap-2 dark:border-emerald-500 dark:text-emerald-200 dark:hover:bg-slate-800"
                                onClick={() => navigate(`/invoices/${selectedOrder.invoice_id}`)}
                            >
                                <FileText className="h-4 w-4" />
                                مشاهده فاکتور
                            </Button>
                        )}

                        {selectedOrder &&
                            selectedOrder.status === 'pending_payment' && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="border-red-300 text-red-600 hover:bg-red-50 rounded-xl flex items-center gap-2 dark:border-red-500 dark:text-red-300 dark:hover:bg-slate-800"
                                    onClick={() => handleCancelOrder(selectedOrder)}
                                    disabled={cancelLoadingId === selectedOrder.id}
                                >
                                    {cancelLoadingId === selectedOrder.id ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            در حال لغو...
                                        </>
                                    ) : (
                                        <>
                                            <XCircle className="h-4 w-4" />
                                            لغو سفارش
                                        </>
                                    )}
                                </Button>
                            )}

                        <Button
                            onClick={closeModal}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-6"
                        >
                            بستن
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Orders;
